import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, Mail, Sparkles, Users } from "lucide-react";
import { SERVICES, EVENT_PACKAGES, SERVICES_DISCLAIMER } from "@/lib/data";

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#F2EFE9] px-6 md:px-10 lg:px-16 py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="overline mb-5">Services & Packages</p>
            <h2
              data-testid="services-heading"
              className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2A2A]"
            >
              A small, intentional menu — <br />
              <span className="italic text-[#C48B71]">designed</span> around how
              you live.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-[#5C5C5C] text-[1.02rem] leading-[1.8] font-light">
              Every package starts with a free 30-minute call. Custom sessions
              and event collections are always welcome — just ask.
            </p>
          </div>
        </div>

        {/* Service cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 md:gap-y-20">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 0.85,
                ease: [0.22, 1, 0.36, 1],
                delay: (i % 3) * 0.08,
              }}
              className={`flex flex-col ${i % 2 === 1 ? "lg:mt-12" : ""}`}
            >
              <a href="#book" className="block img-hover aspect-[4/5] w-full bg-[#E6E2DA] relative">
                <img
                  src={s.image}
                  alt={s.title}
                  className="h-full w-full object-cover"
                  style={{ objectPosition: s.imagePosition || "center 22%" }}
                />
                {s.badge && (
                  <span
                    data-testid={`service-badge-${s.id}`}
                    className="absolute top-4 left-4 bg-[#C48B71] text-[#F9F8F6] overline px-3 py-1.5 backdrop-blur-sm shadow-[0_2px_12px_rgba(0,0,0,0.15)]"
                  >
                    ★ {s.badge}
                  </span>
                )}
              </a>
              <div className="mt-6 flex items-baseline justify-between gap-4">
                <h3 className="serif text-2xl md:text-[1.7rem] font-light text-[#2A2A2A] leading-tight">
                  {s.title}
                </h3>
                <span className="serif text-2xl text-[#C48B71] whitespace-nowrap">
                  {s.price}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2 text-[#5C5C5C]">
                <Calendar size={12} strokeWidth={1.4} />
                <span className="overline text-[#5C5C5C]">{s.duration}</span>
              </div>
              <p className="serif italic text-[1.02rem] text-[#5C5C5C] mt-3 leading-snug">
                {s.tagline}
              </p>
              {s.teamRate && (
                <div
                  data-testid={`service-teamrate-${s.id}`}
                  className="mt-4 inline-flex items-center gap-2 border border-[#C48B71] bg-[#C48B71]/5 px-3 py-2 text-[0.78rem] tracking-[0.04em] text-[#2A2A2A]"
                >
                  <Users size={13} strokeWidth={1.5} className="text-[#C48B71] shrink-0" />
                  {s.teamRate}
                </div>
              )}
              <p className="mt-4 text-[#5C5C5C] text-[0.95rem] leading-[1.75] font-light">
                {s.description}
              </p>
              <ul className="mt-5 space-y-2">
                {s.bullets.map((b) => (
                  <li
                    key={b}
                    className="text-[#2A2A2A] text-[0.92rem] flex items-start gap-3 font-light"
                  >
                    <span className="h-1 w-1 mt-[8px] bg-[#C48B71] rounded-full shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <a
                href="#book"
                data-testid={`service-cta-${s.id}`}
                className="mt-6 inline-flex items-center gap-2 overline link-underline text-[#2A2A2A] w-fit"
              >
                Book this session
                <ArrowUpRight size={14} strokeWidth={1.4} />
              </a>
            </motion.article>
          ))}
        </div>

        {/* Event packages callout */}
        <motion.div
          data-testid="event-packages"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-24 md:mt-32 grid grid-cols-12 gap-6 md:gap-10 border-t border-[#E6E2DA] pt-16 items-center"
        >
          {EVENT_PACKAGES.image && (
            <div className="col-span-12 md:col-span-4 img-hover aspect-[4/5] w-full bg-[#E6E2DA]">
              <img
                src={EVENT_PACKAGES.image}
                alt="Special Event Vision · ManeVzion Photography"
                className="h-full w-full object-cover"
                style={{ objectPosition: "center 18%" }}
              />
            </div>
          )}
          <div className="col-span-12 md:col-span-4">
            <p className="overline mb-4 text-[#C48B71]">Custom Coverage</p>
            <h3 className="serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight text-[#2A2A2A]">
              {EVENT_PACKAGES.title}
            </h3>
            <p className="serif italic text-lg text-[#5C5C5C] mt-3">
              {EVENT_PACKAGES.examples}
            </p>
          </div>

          <div className="col-span-12 md:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
            <div className="border-l-2 border-[#C48B71] pl-5">
              <p className="overline mb-1">Starting at</p>
              <p className="serif text-3xl text-[#2A2A2A]">$200</p>
              <p className="text-[#5C5C5C] text-sm font-light mt-1">
                2 hours of coverage
              </p>
            </div>
            <div className="border-l-2 border-[#E6E2DA] pl-5">
              <p className="overline mb-1">Each additional hour</p>
              <p className="serif text-3xl text-[#2A2A2A]">+$100</p>
              <p className="text-[#5C5C5C] text-sm font-light mt-1">
                As many as you need
              </p>
            </div>
            <p className="sm:col-span-2 text-[#5C5C5C] text-[0.95rem] leading-[1.8] font-light">
              {EVENT_PACKAGES.note}
            </p>
            <a
              href="mailto:manevzion@gmail.com?subject=Event%20Package%20Inquiry"
              data-testid="events-email-link"
              className="sm:col-span-2 btn-outline w-fit"
            >
              <Mail size={14} strokeWidth={1.4} /> Email for event pricing
            </a>
          </div>
        </motion.div>

        {/* Add-ons strip */}
        <motion.div
          data-testid="addons-strip"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="bg-white border border-[#E6E2DA] p-6 flex items-start gap-4">
            <Sparkles size={18} strokeWidth={1.2} className="text-[#C48B71] mt-1 shrink-0" />
            <div>
              <p className="overline">Add-On</p>
              <p className="serif text-xl text-[#2A2A2A] mt-1">Prints à la carte</p>
              <p className="text-[#5C5C5C] text-sm font-light mt-1">
                Order any image as a print — sizing and finishes by request.
              </p>
            </div>
          </div>
          <div className="bg-white border border-[#E6E2DA] p-6 flex items-start gap-4">
            <Sparkles size={18} strokeWidth={1.2} className="text-[#C48B71] mt-1 shrink-0" />
            <div>
              <p className="overline">Upgrade</p>
              <p className="serif text-xl text-[#2A2A2A] mt-1">"All of Them" digital package</p>
              <p className="text-[#5C5C5C] text-sm font-light mt-1">
                Want every edited image from your gallery? Upgrade to the full set.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <p
          data-testid="services-disclaimer"
          className="mt-12 text-center text-xs text-[#5C5C5C] font-light italic max-w-3xl mx-auto leading-[1.8]"
        >
          {SERVICES_DISCLAIMER}
        </p>
      </div>
    </section>
  );
};

export default Services;
